import { JobApplication } from "../../Entities/job-application.entity";
import { InterviewRoundTab, InterviewSession } from "../../Interface/Index";
import { EventDispatcher } from "../../Utils/event-dispatcher";
import { formatDate } from "../../Utils/generic-functions";
import messages from "./messages";

export default class AgentService extends EventDispatcher {
  public static EVENTS = {
    STATE_CHANGED: "state_changed",
    ACTION_BUTTON_CLICKED: "action_button_clicked",
  };
  private static instance: AgentService;

  currentMessage: string = "";
  currentAction: string = "";

  private lastMessageTemplate: string = "";
  private lastMessageProps: any = {};
  private lastAction: string = "";

  private constructor() {
    super();
    this.setCurrentMessage(
      messages.USER_WELCOME_MESSAGE,
      { name: "there" },
      ""
    );
  }

  static getInstance(): AgentService {
    if (!AgentService.instance) {
      AgentService.instance = new AgentService();
    }
    return AgentService.instance;
  }

  getCurrentMessage() {
    return this.currentMessage;
  }

  setCurrentMessage(message: string, props: any, action: string): string {
    let msg = `${message}`;
    for (const key in props) {
      msg = msg.replace(`{{${key}}}`, props[key]);
    }
    const propsUnchanged = JSON.stringify(this.lastMessageProps) === JSON.stringify(props);
    if (
      this.lastMessageTemplate === message &&
      propsUnchanged &&
      this.lastAction === action
    ) {
      return this.currentMessage;
    }
    this.currentMessage = msg;
    this.currentAction = action;
    this.lastMessageTemplate = message;
    this.lastMessageProps = { ...props };
    this.lastAction = action;
    this.dispatch(AgentService.EVENTS.STATE_CHANGED, msg);
    return this.currentMessage;
  }

  onActionButtonClicked(action: string) {
    this.dispatch(AgentService.EVENTS.ACTION_BUTTON_CLICKED, action);
  }

  setAgentMessageBasedOnJobApplication(
    appliedJob: JobApplication | null,
    interviewSessions: InterviewSession[],
    enabledInterviewRoundTabs: InterviewRoundTab[],
    applicationId: string | null
  ) {
    // Handle user not applied for the job
    if (!appliedJob) {
      console.log("Agent message - User has not applied for the job");
      return this.setCurrentMessage(messages.JOB_NOT_APPLIED, "", "Apply Now");
    }

    // Handle application status rejected
    if (appliedJob && appliedJob.status === "rejected") {
      console.log("Agent message - Application rejected");
      return this.setCurrentMessage(messages.FINAL_RESULT_UNQUALIFIED, "", "");
    }

    // Handle application status hired
    if (appliedJob && appliedJob.status === "hired") {
      console.log("Agent message - Application hired");
      return this.setCurrentMessage(messages.FINAL_RESULT_QUALIFIED, "", "");
    }

    // Handle application status shortlisted
    if (appliedJob && appliedJob.status === "shortlisted") {
      console.log("Agent message - Application shortlisted");
      return this.setCurrentMessage(messages.ALL_ROUND_CLEARED, "", "");
    }

    // Handle partially_qualified case
    if (
      appliedJob &&
      appliedJob.status === "applied" &&
      appliedJob.questionnaireStatus === "partially_qualified"
    ) {
      console.log("Agent message - Application partially qualified");
      return this.setCurrentMessage(
        messages.EVALUATION_PARTIALLY_QUALIFIED,
        "",
        ""
      );
    }

    // Handle inetrview slot is not selected
    if (
      appliedJob &&
      appliedJob.status === "interview" &&
      !appliedJob.interviewSchedule
    ) {
      console.log("Agent message - Interview slot not selected");
      return this.setCurrentMessage(
        messages.WAITING_FOR_INTERVIEW_SCHEDULE,
        "",
        "Select Slot"
      );
    }

    // Handle interview slot booked
    if (
      appliedJob &&
      appliedJob.status === "interview" &&
      appliedJob.interviewSchedule
    ) {
      const date = formatDate(appliedJob.interviewSchedule);
      const props = { interview_slot: String(date) };
      console.log(`Agent message - Interview slot booked for date: ${date}`);
      return this.setCurrentMessage(
        messages.INTERVIEW_SCHEDULED_UPCOMING,
        props,
        "View Schedule"
      );
    }

    // Handle messages based on interview session
    if (appliedJob && interviewSessions && interviewSessions.length > 0) {
      const latestSessionsByRoundId = new Map<
        string,
        (typeof interviewSessions)[0]
      >();

      for (const session of interviewSessions) {
        const roundId = session.interviewRoundId;
        if (!roundId) continue;

        const existing = latestSessionsByRoundId.get(roundId);

        if (
          !existing ||
          new Date(session.createdAt) > new Date(existing.createdAt)
        ) {
          latestSessionsByRoundId.set(roundId, session);
        }
      }

      const sortedInterviewSessions = Array.from(
        latestSessionsByRoundId.values()
      ).sort((a, b) => a.interviewRoundNumber - b.interviewRoundNumber);

      // Handle any one session is failed
      const isFailed = sortedInterviewSessions.some(
        (session) => session.status === "unqualified"
      );

      if (isFailed && appliedJob.questionnaireStatus === 'unqualified') {
        return this.setCurrentMessage(messages.EVALUATION_UNQUALIFIED, "", "");
      }

      // Handle pending interview session
      const lastPendingSession = sortedInterviewSessions.find(
        (session) => session.status === "pending"
      );
      const roundName = enabledInterviewRoundTabs.find(
        (round) => round.roundId === lastPendingSession?.interviewRoundId
      )?.roundName;
      if (lastPendingSession) {
        console.log("Pending interview session");
        return this.setCurrentMessage(
          messages.WAITING_FOR_INTERVIEW_ROUND_COMPLETION,
          { roundName: roundName },
          "View Questions"
        );
      }
    }

    if (
      applicationId &&
      appliedJob &&
      appliedJob.status === "applied" &&
      !appliedJob.profileMatchStatus &&
      interviewSessions.length === 0
    ) {
      console.log("User applied for the job");
      return this.setCurrentMessage(
        messages.CV_NOT_UPLOADED,
        "",
        "Upload Resume"
      );
    }

    // Handle user applied but CV not uploaded
    if (!appliedJob.profileMatchStatus && interviewSessions.length === 0) {
      console.log("CV not uploaded");
      return this.setCurrentMessage(
        messages.CV_NOT_UPLOADED,
        "",
        "Upload Resume"
      );
    }

    // Handle CV uploaded but not matched
    if (
      appliedJob.profileMatchStatus?.status === "unqualified" &&
      interviewSessions.length === 0
    ) {
      console.log("CV uploaded but not matched (unqualified)");
      return this.setCurrentMessage(messages.MATCH_RESULT_UNQUALIFIED, "", "");
    }

    // Handle profile qualified but assessments not started yet
    if (
      appliedJob.profileMatchStatus?.status === "qualified" &&
      interviewSessions.length === 0 &&
      enabledInterviewRoundTabs.length > 0
    ) {
      console.log("Profile qualified, pending assessments");
      return this.setCurrentMessage(
        messages.PROFILE_QUALIFIED_PENDING_ASSESSMENT,
        "",
        "Proceed to Assessment"
      );
    }

    return this.setCurrentMessage(messages.UNKNOWN_CONDITION, "", "Contact Us");
  }
}
